/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { District } from 'src/shared/models/district/district';
import { getDistricts } from 'src/shared/services/districtService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';

type DistrictDataGridProps = {
  pageSize?: number;
  loadingCallback: (loading: boolean) => void;
};

export const DistrictDataGrid = (props: DistrictDataGridProps) => {
  const { pageSize = DEFAULT_PAGE_SIZE, loadingCallback } = props;
  const { t } = useTranslation(['user', 'member']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'countyAss', sort: 'asc' }]);

  const [rows, setRows] = useState<District[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: pageSize || DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);

    try {
      const result = await getDistricts(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, false);
      // setSelectionModel(() => memberResult.map((r) => r.id));

      if (isMounted()) {
        const districtResult = extendObjectWithId(result.data.results, 'countyAss_id');
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          setRows(districtResult);
        }, 100);
      }
    } catch (error) {
      if (isMounted()) updateData('loading', false);
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [loadingCallback, data.page, data.pageSize, sortModel, isMounted]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.OrganizationDistricts}/${params.row.countyAss_id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'countyAss',
      headerName: t('district:tableHeaders.name'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.countyAss || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        '& .MuiDataGrid-cell': {
          maxHeight: 'fit-content !important',
          overflow: 'auto',
          // whiteSpace: 'initial !important',
          lineHeight: '16px !important',
          paddingTop: '10px !important',
          paddingBottom: '10px !important',
          display: 'flex !important',
          alignItems: 'flex-start !important',
        },
        '& .MuiDataGrid-viewport, & .MuiDataGrid-row, & .MuiDataGrid-renderingZone': {
          maxHeight: 'fit-content !important',
        },
      }}
    >
      <DataGrid<District>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        sortModel={sortModel}
        checkboxSelection={false}
        onSelectionModelChange={() => ConsoleHelper.log()}
        selectionModel={[]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        onRowClick={onRowClick}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
};
